/* React Query rond de datalaag van Websitecontent (ADR 0024). De client is de gewone CRM-client:
 * de sessie van de ingelogde gebruiker, RLS laat alleen het CRM-team (admin, super_admin, crm_user)
 * lezen en schrijven. Dit is de enige plek in src/features/website die de Supabase-client importeert. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { haalDoorverwijzingen, haalPaginas, type Concept as DoorverwijzingConcept, type Doorverwijzing, type Wijziging } from './data/doorverwijzingen';
import { haalInstellingen, type Concept, type Stand } from './data/instellingen';
import { haalMedia, haalVerwijzingen, maakRij, uploadBestand, type Bestand, type Invoer, type MediaBestand, type MediaClient } from './data/media';
import { haalVastePagina, haalVastePaginas } from './data/paginas';
import { haalOverzicht, type Overzicht, type OverzichtClient, type Venster } from './data/overzicht';
import { haalLijst, haalRij, type ContentSoort, type Rij, type WebsiteClient } from './data/website';
import {
  maakDoorverwijzingEnVervers, maakNieuwEnVervers, slaInstellingenOpEnVervers, slaOpEnVervers, slaPaginaOpEnVervers, verwijderDoorverwijzingEnVervers,
  wijzigDoorverwijzingEnVervers, wijzigMediaEnVervers, type VerversClient,
} from './ververs';

/* De gegenereerde types kennen alleen public; schema website staat buiten types.ts. */
const client = supabase as unknown as WebsiteClient & VerversClient & MediaClient & OverzichtClient;

/** Wie de content en de instellingen mag wijzigen: het CRM-team. RLS dwingt hetzelfde af; dit zet
 *  alleen de knoppen uit voor wie het toch niet mag. */
export function useRechten() {
  const { userRole } = useAuth();
  return { magBewerken: userRole === 'admin' || userRole === 'super_admin' || userRole === 'crm_user' };
}

/** Het websitescherm (ADR 0034): één samenvatting per venster, een minuut vers. */
export const useOverzicht = (dagen: Venster) =>
  useQuery<Overzicht, Error>({ queryKey: ['website', 'overzicht', dagen], queryFn: () => haalOverzicht(client, dagen), staleTime: 60_000, retry: 1 });

const sleutel = (soort: ContentSoort, id?: string) => (id ? ['website', soort.sleutel, id] : ['website', soort.sleutel]);

/** Eén herkansing: een leesfout (bv. geen recht op het schema) hoort snel op de pagina te staan. */
export const useLijst = (soort: ContentSoort | null) =>
  useQuery({ queryKey: soort ? sleutel(soort) : ['website', 'geen'], queryFn: () => haalLijst(client, soort!), enabled: !!soort, staleTime: 30_000, retry: 1 });

export const useRij = (soort: ContentSoort | null, id: string | undefined) =>
  useQuery({
    queryKey: soort && id ? sleutel(soort, id) : ['website', 'geen'],
    queryFn: () => haalRij(client, soort!, id!),
    enabled: !!soort && !!id,
    staleTime: 0,
    // Niet verversen bij terugkeer naar het tabblad: er kan een half ingevuld formulier open staan.
    refetchOnWindowFocus: false,
    retry: 1,
  });

/* Opslaan en aanmaken vragen de site daarna om te verversen (ververs.ts, ADR 0031). De mutatie is
 * klaar zodra de rij is opgeslagen; `verversing` in de uitkomst vertelt later wat de site deed. */
export function useOpslaan(soort: ContentSoort) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vorige, concept }: { vorige: Rij; concept: Rij }) => slaOpEnVervers(client, soort, vorige, concept),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website', soort.sleutel] }),
  });
}

export function useNieuw(soort: ContentSoort) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (concept: Rij) => maakNieuwEnVervers(client, soort, concept),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website', soort.sleutel] }),
  });
}

/* De vaste pagina's van de site (data/paginas.ts). Een pagina kent geen concept en wordt nooit
 * aangemaakt: er is alleen lezen en opslaan. */
const PAGINAS = ['website', 'paginas'];

export const useVastePaginas = () => useQuery({ queryKey: PAGINAS, queryFn: () => haalVastePaginas(client), staleTime: 30_000, retry: 1 });

export const useVastePagina = (id: string | undefined) =>
  useQuery({
    queryKey: id ? [...PAGINAS, id] : ['website', 'geen'],
    queryFn: () => haalVastePagina(client, id!),
    enabled: !!id,
    staleTime: 0,
    // Niet verversen bij terugkeer naar het tabblad: er kan een half ingevuld formulier open staan.
    refetchOnWindowFocus: false,
    retry: 1,
  });

export function usePaginaOpslaan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vorige, concept }: { vorige: Rij; concept: Rij }) => slaPaginaOpEnVervers(client, vorige, concept),
    // De nieuwe rij staat er meteen: het formulier is dan weer gelijk aan de database.
    onSuccess: (uitkomst) => {
      qc.setQueryData([...PAGINAS, uitkomst.rij.id], uitkomst.rij);
      return qc.invalidateQueries({ queryKey: PAGINAS, exact: true });
    },
  });
}

/* De instellingen van de site (data/instellingen.ts). Eén query voor alle bekende sleutels. */
const INSTELLINGEN = ['website', 'instellingen'];

export const useInstellingen = () =>
  // Niet verversen bij terugkeer naar het tabblad: er kan een half ingevuld formulier open staan.
  useQuery({ queryKey: INSTELLINGEN, queryFn: () => haalInstellingen(client), staleTime: 0, refetchOnWindowFocus: false, retry: 1 });

export function useInstellingenOpslaan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stand, concept }: { stand: Stand; concept: Concept }) => slaInstellingenOpEnVervers(client, stand, concept),
    // De nieuwe stand staat er meteen: het formulier is dan weer gelijk aan de database.
    onSuccess: (uitkomst) => qc.setQueryData(INSTELLINGEN, uitkomst.stand),
  });
}

/* De doorverwijzingen (data/doorverwijzingen.ts). Eén query voor de tabel en voor de adressen waarop
 * een pagina staat: het scherm keurt een nieuwe doorverwijzing tegen allebei. */
const DOORVERWIJZINGEN = ['website', 'doorverwijzingen'];

export const useDoorverwijzingen = () =>
  useQuery({
    queryKey: DOORVERWIJZINGEN,
    queryFn: async () => {
      const [doorverwijzingen, paginas] = await Promise.all([haalDoorverwijzingen(client), haalPaginas(client)]);
      return { doorverwijzingen, paginas };
    },
    staleTime: 0,
    // Niet verversen bij terugkeer naar het tabblad: er kan een half ingevuld formulier open staan.
    refetchOnWindowFocus: false,
    retry: 1,
  });

export function useDoorverwijzingMaken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (concept: DoorverwijzingConcept) => maakDoorverwijzingEnVervers(client, concept),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOORVERWIJZINGEN }),
  });
}

export function useDoorverwijzingWijzigen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vorige, concept }: { vorige: Doorverwijzing; concept: Wijziging }) => wijzigDoorverwijzingEnVervers(client, vorige, concept),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOORVERWIJZINGEN }),
  });
}

/** Een doorverwijzing verwijderen: het enige in Websitecontent dat verwijderd kan worden (ADR 0024). */
export function useDoorverwijzingVerwijderen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (van: string) => verwijderDoorverwijzingEnVervers(client, van),
    // Ook na een fout opnieuw lezen: was de rij al weg, dan verdwijnt ze zo ook uit de lijst.
    onSettled: () => qc.invalidateQueries({ queryKey: DOORVERWIJZINGEN }),
  });
}

/* De mediabibliotheek (data/media.ts). De site gebruikt de beschrijving van een beeld en niet de naam:
 * na een gewijzigde beschrijving ververst de site (ververs.ts), na uploaden of alleen een andere naam niet. */
const MEDIA = ['website', 'media'];

export const useMedia = () =>
  // Niet verversen bij terugkeer naar het tabblad: er kan een bestand open staan om te bewerken.
  useQuery({ queryKey: MEDIA, queryFn: () => haalMedia(client), staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 });

/* Waar een bestand in de content staat. Een eigen sleutel, los van de lijst: na een upload hoeft de
 * content niet opnieuw gelezen te worden. */
export const useMediaVerwijzingen = () =>
  useQuery({ queryKey: ['website', 'mediaverwijzingen'], queryFn: () => haalVerwijzingen(client), staleTime: 0, refetchOnWindowFocus: false, retry: 1 });

export function useMediaUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bestand, invoer, alleenBeelden }: { bestand: Bestand; invoer: Invoer; alleenBeelden?: boolean }) => uploadBestand(client, bestand, invoer, { alleenBeelden }),
    // Ook een bestand zonder rij is een geslaagde mutatie: dan valt er niets nieuws te lezen.
    onSuccess: (uitkomst) => (uitkomst.rij ? qc.invalidateQueries({ queryKey: MEDIA }) : undefined),
  });
}

/** De rij alsnog aanmaken voor een bestand dat al in de opslag staat. */
export function useMediaRij() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pad, invoer }: { pad: string; invoer: Invoer }) => maakRij(client, pad, invoer),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA }),
  });
}

export function useMediaWijzigen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vorige, concept }: { vorige: MediaBestand; concept: Invoer }) => wijzigMediaEnVervers(client, vorige, concept),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA }),
  });
}
